import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Table,
  Tag,
  Space,
  Select,
  Popconfirm,
  Input,
  Avatar,
  Upload,
  Typography,
  message,
} from "antd";
import SheetDrawer from "../../components/sheet/SheetDrawer";
import { PlusOutlined, PictureOutlined } from "@ant-design/icons";

import {
  fetchCustomizationOptions,
  createCustomizationOption,
  updateCustomizationOption,
  deleteCustomizationOption,
} from "../../features/packages/packagesThunk";

const { Text } = Typography;
const { TextArea } = Input;

const CATEGORY_OPTIONS = ["HOTEL", "CAR", "ROOM", "FOOD", "TOUR", "OTHERS"];
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

const emptyEntry = {
  category: "HOTEL",
  name: "",
  description: "",
  mediaUrl: "",
  imageFile: null,
  removeImage: false,
};

const CustomizationsPanel = () => {
  const dispatch = useDispatch();
  const { customizationOptions, customizationsLoading, customizationActionLoading } =
    useSelector((state) => state.packages);

  const [categoryFilter, setCategoryFilter] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null); // the record being edited, or null for create
  const [draft, setDraft] = useState(emptyEntry);

  useEffect(() => {
    dispatch(fetchCustomizationOptions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setDraft(emptyEntry);
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setDraft({
      category: record.category,
      name: record.name,
      description: record.description || "",
      mediaUrl: record.media_url || "",
      imageFile: null,
      removeImage: false,
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    let data;
    if (draft.imageFile) {
      // Multipart: the "image" file sets/replaces media_url outright.
      data = new FormData();
      data.append("category", draft.category);
      data.append("name", draft.name);
      if (draft.description) data.append("description", draft.description);
      data.append("image", draft.imageFile);
    } else {
      data = {
        category: draft.category,
        name: draft.name,
        description: draft.description || undefined,
        ...(draft.removeImage ? { media_url: null } : {}),
      };
    }

    const action = editing
      ? updateCustomizationOption({ id: editing.id, data })
      : createCustomizationOption(data);

    const result = await dispatch(action);
    if (!result.error) setDrawerOpen(false);
  };

  const handleDelete = (id) => dispatch(deleteCustomizationOption(id));

  const imageFileList = draft.imageFile
    ? [
        {
          uid: "new",
          name: draft.imageFile.name,
          status: "done",
          url: URL.createObjectURL(draft.imageFile),
        },
      ]
    : draft.mediaUrl && !draft.removeImage
      ? [{ uid: "existing", name: "image", status: "done", url: draft.mediaUrl }]
      : [];

  const handleImageChange = ({ fileList }) => {
    const last = fileList[fileList.length - 1];
    if (!last) {
      setDraft((d) => ({ ...d, imageFile: null, removeImage: !!d.mediaUrl }));
      return;
    }
    if (last.originFileObj) {
      setDraft((d) => ({ ...d, imageFile: last.originFileObj, removeImage: false }));
    }
  };

  const data = categoryFilter
    ? (customizationOptions || []).filter((o) => o.category === categoryFilter)
    : customizationOptions || [];

  const columns = [
    {
      title: "Image",
      key: "media",
      width: 64,
      render: (_, record) =>
        record.media_url ? (
          <Avatar shape="square" size="large" src={record.media_url} />
        ) : (
          <Avatar shape="square" size="large" icon={<PictureOutlined />} />
        ),
    },
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category) => <Tag color="blue">{category}</Tag>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (d) => d || <Text type="secondary">—</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this customization option?"
            description="This removes it from every package currently using it — this can't be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record.id)}
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "16px 0",
        }}
      >
        <Select
          placeholder="Filter by category"
          allowClear
          style={{ width: 220 }}
          value={categoryFilter}
          onChange={setCategoryFilter}
          options={CATEGORY_OPTIONS.map((c) => ({ value: c, label: c }))}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Create Customization Option
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={customizationsLoading}
        columns={columns}
        dataSource={data}
        pagination={{ pageSize: 10 }}
      />

      <SheetDrawer
        title={editing ? "Edit Customization Option" : "Create Customization Option"}
        width={420}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Category *</div>
          <Select
            style={{ width: "100%" }}
            value={draft.category}
            onChange={(category) => setDraft((d) => ({ ...d, category }))}
            options={CATEGORY_OPTIONS.map((c) => ({ value: c, label: c }))}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Name *</div>
          <Input
            placeholder="e.g. Sea Breeze Resort"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Description</div>
          <TextArea
            rows={3}
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Image</div>
          <Upload
            listType="picture-card"
            fileList={imageFileList}
            maxCount={1}
            accept=".jpg,.jpeg,.png,.gif,.webp"
            beforeUpload={(file) => {
              if (file.size > MAX_IMAGE_BYTES) {
                message.error("Image must be 2MB or smaller");
                return Upload.LIST_IGNORE;
              }
              return false;
            }}
            onChange={handleImageChange}
          >
            {imageFileList.length >= 1 ? null : (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </div>
            )}
          </Upload>
        </div>

        <Button
          type="primary"
          block
          loading={customizationActionLoading}
          disabled={!draft.category || !draft.name.trim()}
          onClick={handleSave}
        >
          {editing ? "Save Changes" : "Create"}
        </Button>
      </SheetDrawer>
    </div>
  );
};

export default CustomizationsPanel;
